import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Moon, Leaf, Bird, Flame, Utensils, Compass,
  Coffee, Music, Gamepad2, Target
} from 'lucide-react';

const SAFARI_VIDEO = 'https://customer-assets.emergentagent.com/job_b06f7103-614a-40d0-b0be-da5d75ce1ae0/artifacts/kkkry9x5_Wildlife%20safari.mp4';
const SAFARI_POSTER = '/posters/safari.jpg';
const NIGHT_VIDEO = 'https://customer-assets.emergentagent.com/job_b06f7103-614a-40d0-b0be-da5d75ce1ae0/artifacts/y5c6abs7_Night%20safari.mp4';
const NIGHT_POSTER = '/posters/night.jpg';
const PLANTATION_IMG = 'https://images.unsplash.com/photo-1758390286142-5462148c8fe8?w=1600&q=80';

const FEATURED = [
  {
    n: '01',
    title: 'Wildlife Safari',
    sub: 'Jeep · Tracker-guided',
    body: 'Tracker-guided jeep safaris through ancient teak forests. Wildlife safari in Nagarhole, Tholpetty & Kabini available — listen for elephant rumbles and watch for the tiger\'s pugmarks fresh on the dust road.',
    img: SAFARI_POSTER,
    video: SAFARI_VIDEO,
    poster: SAFARI_POSTER,
    icon: Compass,
    tilt: '-rotate-2',
  },
  {
    n: '02',
    title: 'Night Safari',
    sub: 'After dark · Spotlight · Guided',
    body: 'When the forest changes shifts — civets, leopards, sloth bears. A different sanctuary, one you can only hear before you see.',
    img: NIGHT_POSTER,
    video: NIGHT_VIDEO,
    poster: NIGHT_POSTER,
    icon: Moon,
    tilt: 'rotate-2',
  },
  {
    n: '03',
    title: 'Plantation Walk',
    sub: 'Morning · 90 min · Naturalist',
    body: 'A slow walk through coffee, pepper and cardamom. The Western Ghats reveal themselves in the small things — a beetle, a fern, a hornbill\'s call.',
    img: PLANTATION_IMG,
    icon: Leaf,
    tilt: '-rotate-1',
  },
];

const SMALL = [
  { icon: Bird, label: 'Bird Watching', sub: 'Western Ghats endemics' },
  { icon: Flame, label: 'Campfire', sub: 'Nightly · open courtyard' },
  { icon: Utensils, label: 'Open-Fire BBQ', sub: 'Chef-led · on request' },
  { icon: Music, label: 'Live Music', sub: 'Acoustic · till 10pm' },
  { icon: Coffee, label: 'Café', sub: 'Estate-grown brews' },
  { icon: Utensils, label: 'Fine Dining', sub: 'Forest-to-table menus' },
  { icon: Target, label: 'Cricket & Badminton', sub: 'On-property courts' },
  { icon: Gamepad2, label: 'Board Games', sub: 'Quiet evenings indoors' },
];

const LazyVideo = ({ src, poster, testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Begin buffering as soon as the hero video is ready (so it's primed before the user scrolls down).
    const prime = () => { try { el.preload = 'auto'; el.load(); } catch { /* noop */ } };
    if (window.__tuskerHeroReady) prime();
    else window.addEventListener('tusker:hero-ready', prime, { once: true });

    // Play seamlessly when it enters (or is about to enter) the viewport; pause when it leaves.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { el.preload = 'auto'; el.play().catch(() => {}); }
        else { el.pause(); }
      },
      { threshold: 0.01, rootMargin: '300px 0px' }
    );
    io.observe(el);
    return () => { io.disconnect(); window.removeEventListener('tusker:hero-ready', prime); };
  }, []);
  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      className="w-full h-full object-cover"
      loop
      muted
      playsInline
      preload="none"
      data-testid={testId}
    />
  );
};

const FeaturedItem = ({ item, flip }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
    className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${flip ? 'lg:[&>*:first-child]:order-2' : ''}`}
    data-testid={`activity-featured-${item.n}`}
  >
    {/* polaroid image */}
    <div className="lg:col-span-7">
      <motion.div
        whileHover={{ rotate: 0, scale: 1.02 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`polaroid ${item.tilt} max-w-[640px] ${flip ? 'ml-auto' : ''}`}
        data-caption={`— ${item.title} —`}
      >
        <div className="aspect-[4/3] overflow-hidden bg-[#1A2421]">
          {item.video ? (
            <LazyVideo src={item.video} poster={item.poster} testId={`activity-video-${item.n}`} />
          ) : (
            <img src={item.img} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
          )}
        </div>
      </motion.div>
    </div>

    {/* text */}
    <div className="lg:col-span-5">
      <div className="flex items-center gap-4 mb-5">
        <div className="numeral text-5xl text-[#D4AF37]/40">{item.n}</div>
        <item.icon size={22} className="text-[#D4AF37]" />
      </div>
      <h3 className="font-serif text-3xl md:text-5xl font-light text-[#E8E3D9] mb-3 leading-tight">
        {item.title}
      </h3>
      <div className="overline mb-5 text-[10px]">{item.sub}</div>
      <p className="text-[#8E9A95] leading-[1.85] text-[15px] mb-6">
        {item.body}
      </p>
      <div className="brass-rule w-20" />
    </div>
  </motion.div>
);

const ActivitiesSection = () => {
  return (
    <section id="activities" className="relative bg-[#121816] py-32 md:py-44 overflow-hidden" data-testid="activities-section">

      <div className="max-w-[1480px] mx-auto px-6 md:px-12">

        {/* heading */}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 lg:col-span-3">
            <div className="overline">Chapter III</div>
            <div className="numeral text-7xl md:text-8xl text-[#D4AF37]/30 mt-4">03</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-[#E8E3D9] leading-[1.05]"
            >
              The tracker's<br/>
              <span className="italic text-[#D4AF37]">journal.</span>
            </motion.h2>
            <p className="mt-6 max-w-xl text-[#8E9A95] text-[15px] leading-relaxed">
              Pages torn from a guide's notebook — three signature experiences, a hundred quiet rituals
              in between. Some are scheduled. The best ones aren't.
            </p>
          </div>
        </div>

        {/* featured polaroids — alternating */}
        <div className="space-y-32 md:space-y-44 mb-32">
          {FEATURED.map((item, i) => (
            <FeaturedItem key={item.n} item={item} flip={i % 2 === 1} />
          ))}
        </div>

        {/* small amenities grid */}
        <div className="border-t border-[#D4AF37]/15 pt-16">
          <div className="overline mb-8">Also at the resort</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#D4AF37]/10">
            {SMALL.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-[#121816] p-6 md:p-7 group hover:bg-[#1A2421] transition-colors cursor-default"
                data-testid={`activity-${s.label.toLowerCase().replace(/\s|&/g, '-')}`}
              >
                <s.icon size={20} className="text-[#D4AF37] mb-4 group-hover:scale-110 transition-transform" />
                <div className="font-serif text-lg text-[#E8E3D9] leading-tight">{s.label}</div>
                <div className="text-[11px] tracking-wider text-[#8E9A95] mt-1">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="text-center text-[11px] tracking-[0.2em] uppercase text-[#8E9A95] mt-12">
          Wildlife safari & BBQ available at additional cost · Music permitted until 10:00 pm
        </p>
      </div>
    </section>
  );
};

export default ActivitiesSection;
